import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signupUser } from "../apis/auth";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import {
  UserPlus,
  Shield,
  Eye,
  EyeOff,
  Smartphone,
  Code,
  MessageSquare,
  Zap,
  Sparkles,
  AlertCircle,
} from "lucide-react";
import logo from "../assets/i2.png";

const Signup = () => {
  const [form, setForm] = useState({
    email: "",
    name: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Blutec PING Signup - Create Your Free WhatsApp Messaging Account";
    const description =
      "Sign up for Blutec PING and start free WhatsApp broadcasting, bulk messaging, and chatbot automation. Quick setup—get instant access to smart business tools.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description);
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      toast.error("Passwords do not match.");
      return;
    }
    setIsLoading(true);
    try {
      await signupUser({ ...form, confirmPassword: undefined });
      toast.success("Signup successful! Redirecting to login...");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      const message = err.response?.data?.message || "Signup failed.";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: Smartphone,
      title: "Connect WhatsApp in 1 minute",
      description: "Quick and easy WhatsApp integration",
    },
    {
      icon: Code,
      title: "Send message using API",
      description: "Powerful API for seamless integration",
    },
    {
      icon: MessageSquare,
      title: "AI Chatbot",
      description: "Automate customer conversations",
    },
    {
      icon: Zap,
      title: "Fast & Reliable",
      description: "Lightning-fast message delivery",
    },
  ];

  return (
    <div className="h-screen w-full flex overflow-hidden bg-white">
      {/* Left Section - Animated Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-[#048AAD] via-[#048AAD]/90 to-[#01CC54]">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          {/* Floating Circles */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-white/10"
              style={{
                width: Math.random() * 200 + 100,
                height: Math.random() * 200 + 100,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                x: [0, 20, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 5 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 py-16 text-white">
          {/* Logo with Animation */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <img
              src={logo}
              alt="BluTec PING"
              className="h-20 mb-4 filter brightness-0 invert"
            />
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-5xl font-extrabold mb-4 leading-tight"
          >
            Join BluTec PING
            <br />
            <span className="text-white/90">Start Your Journey</span>
          </motion.h1>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl text-white/90 mb-12 max-w-md"
          >
            Create your account and unlock powerful WhatsApp automation tools to grow your business.
          </motion.p>

          {/* Feature Highlights */}
          <div className="space-y-4">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                  className="flex items-start gap-4 group"
                >
                  <motion.div
                    className="p-3 rounded-xl bg-white/20 backdrop-blur-sm group-hover:bg-white/30 transition-colors"
                    whileHover={{ scale: 1.05, rotate: 5 }}
                  >
                    <Icon size={24} className="text-white" />
                  </motion.div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-white/80 text-sm">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Decorative Sparkles */}
          <motion.div
            className="absolute bottom-20 right-20"
            animate={{
              rotate: [0, 360],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "linear",
            }}
          >
            <Sparkles size={80} className="text-white/20" />
          </motion.div>
        </div>
      </div>

      {/* Right Section - Signup Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-4 sm:px-6 lg:px-12 py-10 sm:py-16 overflow-y-auto bg-gray-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <motion.img
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              src={logo}
              alt="BluTec PING"
              className="h-16 mx-auto mb-4"
            />
            <h1 className="text-3xl font-extrabold text-[#01CC54] mb-2">
              Create Account
            </h1>
            <p className="text-gray-600">
              Join us today and get access to amazing features
            </p>
          </div>

          {/* Desktop Welcome */}
          <div className="hidden lg:block mb-8">
            <h1 className="text-4xl font-extrabold text-gray-900 mb-2">
              Create Account
            </h1>
            <p className="text-gray-600">
              Join us today and get access to amazing features
            </p>
          </div>

          {/* Signup Card */}
          <div className="relative rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-[#048AAD] via-[#048AAD]/90 to-[#01CC54]" />
            <form onSubmit={handleSubmit} className="p-7 space-y-4">
              {/* Security chip */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="flex items-center justify-center mb-4"
              >
                <span className="inline-flex items-center gap-2 rounded-full border border-[#01CC54]/20 bg-[#01CC54]/10 px-3 py-1.5 text-[#01CC54] text-xs font-medium">
                  <Shield size={14} />
                  Secure Account Registration
                </span>
              </motion.div>

              {/* Error */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 bg-rose-50 border border-red-300 text-red-700 rounded-xl p-3 text-sm font-medium"
                >
                  <AlertCircle size={18} className="flex-shrink-0" />
                  <span>{error}</span>
                </motion.div>
              )}

              {/* Name Field */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <input
                  name="name"
                  type="text"
                  placeholder="Enter your full name"
                  autoComplete="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-base text-gray-900 placeholder-gray-500 shadow-sm outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition"
                />
              </motion.div>

              {/* Phone Field */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
              >
                <input
                  name="phone"
                  type="text"
                  placeholder="Enter your phone number"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-base text-gray-900 placeholder-gray-500 shadow-sm outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition"
                />
              </motion.div>

              {/* Email Field */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <input
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  autoComplete="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-base text-gray-900 placeholder-gray-500 shadow-sm outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition"
                />
              </motion.div>

              {/* Password Field */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                className="relative"
              >
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 pr-12 text-base text-gray-900 placeholder-gray-500 shadow-sm outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#01CC54] transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </motion.div>

              {/* Confirm Password Field */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="relative"
              >
                <input
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm password"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 pr-12 text-base text-gray-900 placeholder-gray-500 shadow-sm outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#01CC54] transition-colors"
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </motion.div>

              {/* Submit Button */}
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55 }}
                type="submit"
                disabled={isLoading}
                className={`w-full flex items-center justify-center gap-2 rounded-xl py-3 text-lg font-semibold text-white bg-gradient-to-r from-[#048AAD] to-[#048AAD]/90 shadow-lg transition-all hover:shadow-xl hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#048AAD]/30 ${
                  isLoading ? "opacity-60 cursor-not-allowed" : ""
                }`}
                whileHover={!isLoading ? { scale: 1.02 } : {}}
                whileTap={!isLoading ? { scale: 0.98 } : {}}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                    <span>Creating Account...</span>
                  </>
                ) : (
                  <>
                    <UserPlus size={18} />
                    <span>Create Account</span>
                  </>
                )}
              </motion.button>

              {/* Security Message */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="mt-2 text-gray-500 text-xs text-center"
              >
                <span className="mr-1">🔒</span>Your data is secure and protected
              </motion.div>

              {/* Sign In Link */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.65 }}
                className="text-sm text-center mt-4 text-gray-600"
              >
                Already have an account?{" "}
                <a
                  href="/login"
                  className="text-[#01CC54] font-semibold hover:text-[#01CC54]/80 underline underline-offset-4 transition-colors"
                >
                  Sign In
                </a>
              </motion.p>

              {/* Terms */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="text-xs text-gray-500 text-center mt-2"
              >
                By creating an account, you agree to our terms and conditions
              </motion.p>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Signup;
